import type { CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import logoUrl from '@/shared/assets/logo.svg';
import { cn } from '@/shared/lib/cn';

type LogoProps = {
  /** `brand` = navy block (on the ground or white); `inverse` = white block for the navy sidebar. */
  tone?: 'brand' | 'inverse';
  /** Hide from assistive technology when a parent link or heading already names it. */
  decorative?: boolean;
  className?: string;
};

// The SVG is a navy block with the lettering knocked out, on a square canvas with empty bands
// above and below. It is used as a mask so the block can take a token colour, and `cover` crops
// the square canvas to the 16:9 block. Single quotes: a bundler that inlines the SVG as a data URI
// leaves its double quotes in, which would end a double-quoted url().
const mask: CSSProperties = {
  maskImage: `url('${logoUrl}')`,
  maskSize: 'cover',
  maskPosition: 'center',
  maskRepeat: 'no-repeat',
  WebkitMaskImage: `url('${logoUrl}')`,
  WebkitMaskSize: 'cover',
  WebkitMaskPosition: 'center',
  WebkitMaskRepeat: 'no-repeat',
};

/** The No Conflict Clearance block logo. Set the width with `className` (`w-32`…). */
function Logo({ tone = 'brand', decorative = false, className }: LogoProps) {
  const { t } = useTranslation();
  return (
    <span
      data-slot="logo"
      data-tone={tone}
      {...(decorative ? { 'aria-hidden': true } : { role: 'img', 'aria-label': t('productName') })}
      style={mask}
      className={cn(
        'block aspect-video w-32 shrink-0',
        tone === 'inverse' ? 'bg-sidebar-foreground' : 'bg-primary',
        className,
      )}
    />
  );
}

export { Logo, type LogoProps };
